import { usePageTitle } from "@/hooks/use-page-title";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { useDebounce } from "@/hooks/use-debounce";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Search, Mail, Phone, Trash2, Filter, Edit, ExternalLink, MessageSquare, Save, Loader2 } from "lucide-react";
import { Link } from "wouter";
import CreateContactModal from "@/components/modals/create-contact-modal";
import EditContactModal from "@/components/modals/edit-contact-modal";
import { insertContactSchema } from "@shared/schema";
import { z } from "zod";
import { formatPhoneNumber } from "@/lib/utils";

interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  department: string;
  userId?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  companies?: Array<{
    id: number;
    companyId: number;
    relationshipType: string;
    isPrimary: boolean;
    company: {
      id: number;
      name: string;
      type: string;
    };
  }>;
}

interface ColumnConfig {
  id: string;
  label: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}

const COLUMN_CONFIG: ColumnConfig[] = [
  { id: 'name', label: 'Name', defaultWidth: 180, minWidth: 120, maxWidth: 300 },
  { id: 'company', label: 'Company', defaultWidth: 200, minWidth: 120, maxWidth: 350 },
  { id: 'email', label: 'Email', defaultWidth: 220, minWidth: 150, maxWidth: 350 },
  { id: 'phone', label: 'Phone', defaultWidth: 140, minWidth: 100, maxWidth: 200 },
  { id: 'role', label: 'Role', defaultWidth: 150, minWidth: 100, maxWidth: 250 },
  { id: 'actions', label: 'Actions', defaultWidth: 140, minWidth: 100, maxWidth: 180 },
];

const STORAGE_KEY = 'contacts-column-widths';

function getStoredColumnWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return {};
}

function saveColumnWidths(widths: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch {}
}

function QuickNotePopover({ contactId }: { contactId: number }) {
  const { toast } = useToast();
  const [noteContent, setNoteContent] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: notes = [], isLoading: notesLoading } = useQuery({
    queryKey: ['/api/notes', 'contact', String(contactId)],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/notes/contact/${contactId}?includePrivate=true`);
      return response.json();
    },
    enabled: isOpen,
  });

  const createNoteMutation = useMutation({
    mutationFn: async (noteData: { content: string; entityType: string; entityId: string; isPrivate: boolean }) => {
      const response = await apiRequest('POST', '/api/notes', noteData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notes', 'contact', String(contactId)] });
      setNoteContent("");
      toast({
        title: "Success",
        description: "Note added",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!noteContent.trim()) return;
    createNoteMutation.mutate({
      content: noteContent,
      entityType: 'contact',
      entityId: String(contactId),
      isPrivate: false,
    });
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`button-notes-${contactId}`}>
          <MessageSquare className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Quick Notes</h4>
          <div className="max-h-32 overflow-y-auto space-y-2">
            {notesLoading ? (
              <p className="text-xs text-gray-500">Loading...</p>
            ) : notes.length === 0 ? (
              <p className="text-xs text-gray-500">No notes yet</p>
            ) : (
              notes.slice(0, 3).map((note: any) => (
                <div key={note.id} className="text-xs p-2 bg-gray-50 rounded">
                  <p className="line-clamp-2">{note.content}</p>
                  <p className="text-gray-400 mt-1">{note.author?.firstName} {note.author?.lastName}</p>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Add a quick note..."
              className="text-xs h-16"
            />
          </div>
          <div className="flex justify-between items-center">
            <Link href={`/contacts/${contactId}`}>
              <Button variant="link" size="sm" className="text-xs px-0">
                View all notes
              </Button>
            </Link>
            <Button 
              size="sm" 
              onClick={handleSubmit}
              disabled={!noteContent.trim() || createNoteMutation.isPending}
            >
              {createNoteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Contacts() {
  usePageTitle("Contacts");
  const { toast } = useToast();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const queryClient = useQueryClient();

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const stored = getStoredColumnWidths();
    const defaults: Record<string, number> = {};
    COLUMN_CONFIG.forEach(col => {
      defaults[col.id] = stored[col.id] || col.defaultWidth;
    });
    return defaults;
  });

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const config = COLUMN_CONFIG.find(c => c.id === resizingRef.current!.columnId);
      if (!config) return;
      
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(
        config.minWidth,
        Math.min(config.maxWidth, resizingRef.current.startWidth + diff)
      );
      
      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current!.columnId]: newWidth
      }));
    };
    
    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(prev => {
          saveColumnWidths(prev);
          return prev;
        });
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const getColumnWidth = useCallback((columnId: string) => {
    return columnWidths[columnId] || COLUMN_CONFIG.find(c => c.id === columnId)?.defaultWidth || 100;
  }, [columnWidths]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, authLoading, toast]);

  const {
    data: contactsInfinite,
    isLoading,
    fetchNextPage: contactsFetchNextPage,
    hasNextPage: contactsHasNextPage,
    isFetchingNextPage: contactsIsFetchingNextPage,
  } = useInfiniteQuery<{ data: Contact[]; nextCursor: string | null; totalCount?: number }>({
    queryKey: ['/api/contacts', 'with-companies', { search: debouncedSearch }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams();
      qs.set('include', 'companies');
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      if (pageParam) qs.set('cursor', pageParam as string);
      const response = await apiRequest("GET", `/api/contacts?${qs.toString()}`);
      return response.json();
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    refetchOnMount: "always",
    staleTime: 30000,
  });

  const contacts = useMemo(() =>
    contactsInfinite?.pages.flatMap(p => p.data) ?? [],
    [contactsInfinite]
  );
  const contactsTotalCount = contactsInfinite?.pages[0]?.totalCount ?? contacts.length;

  const createContactMutation = useMutation({
    mutationFn: async (contactData: z.infer<typeof insertContactSchema> & { companies: any[] }) => {
      await apiRequest("POST", "/api/contacts", contactData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/contacts', 'with-companies'] });
      setIsCreateModalOpen(false);
      toast({
        title: "Success",
        description: "Contact created successfully",
      });
    },
    onError: (error) => {
      console.error("Error creating contact:", error);
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      });
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: async (contactData: z.infer<typeof insertContactSchema>) => {
      await apiRequest("PUT", `/api/contacts/${selectedContact?.id}`, contactData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/contacts', 'with-companies'] });
      setIsEditModalOpen(false);
      setSelectedContact(null);
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating contact:", error);
      toast({
        title: "Error",
        description: "Failed to update contact",
        variant: "destructive",
      });
    },
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: number) => {
      await apiRequest("DELETE", `/api/contacts/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/contacts', 'with-companies'] });
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting contact:", error);
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      });
    },
  });

  const handleCreateContact = (contactData: z.infer<typeof insertContactSchema> & { companies: any[] }) => {
    createContactMutation.mutate(contactData);
  };

  const handleUpdateContact = (contactData: z.infer<typeof insertContactSchema>) => {
    updateContactMutation.mutate(contactData);
  };

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setIsEditModalOpen(true);
  };

  const handleDeleteContact = (contactId: number) => {
    if (confirm("Are you sure you want to delete this contact?")) {
      deleteContactMutation.mutate(contactId);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesRole = roleFilter === "all" || 
      contact.companies?.some(c => c.relationshipType === roleFilter);

    return matchesRole;
  });

  const getRoleBadgeColor = (role: string) => {
    const colors: Record<string, string> = {
      owner: "bg-amber-100 text-amber-800",
      billing: "bg-green-100 text-green-800",
      operations: "bg-blue-100 text-blue-800",
      safety: "bg-orange-100 text-orange-800",
      dispatch: "bg-purple-100 text-purple-800",
      compliance: "bg-indigo-100 text-indigo-800",
      emergency: "bg-red-100 text-red-800",
      other: "bg-gray-100 text-gray-800",
    };
    return colors[role] || colors.other;
  };

  if (isLoading || authLoading || !isAuthenticated) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  return (
    <ResponsiveLayout title="Contacts" subtitle="Manage all your contact information in one place" currentPage="contacts">
      <div className="p-4">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Contacts</h2>
          </div>
          <Button onClick={() => setIsCreateModalOpen(true)} data-testid="button-add-contact">
            <Plus className="h-4 w-4 mr-2" />
            Add Contact
          </Button>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="input-search-contacts"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[180px]" data-testid="select-role-filter">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="billing">Billing</SelectItem>
              <SelectItem value="operations">Operations</SelectItem>
              <SelectItem value="safety">Safety</SelectItem>
              <SelectItem value="dispatch">Dispatch</SelectItem>
              <SelectItem value="compliance">Compliance</SelectItem>
              <SelectItem value="emergency">Emergency</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filteredContacts.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <div className="text-gray-500 space-y-3">
                <p>
                  {searchQuery || roleFilter !== "all"
                    ? "No contacts match your search criteria."
                    : "No contacts yet. Click 'Add Contact' to create one."}
                </p>
                {(searchQuery || roleFilter !== "all") && (
                  <Button variant="outline" size="sm" onClick={() => { setSearchQuery(""); setRoleFilter("all"); }}>
                    Clear search
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table className="w-full" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  {COLUMN_CONFIG.map(col => (
                    <col key={col.id} style={{ width: `${getColumnWidth(col.id)}px` }} />
                  ))}
                </colgroup>
                <TableHeader>
                  <TableRow>
                    <TableHead className="relative group" style={{ width: getColumnWidth('name') }}>
                      <div className="pr-2">Name</div>
                      <div 
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                        onMouseDown={(e) => handleResizeStart('name', e)}
                      />
                    </TableHead>
                    <TableHead className="relative group" style={{ width: getColumnWidth('company') }}>
                      <div className="pr-2">Company</div>
                      <div 
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                        onMouseDown={(e) => handleResizeStart('company', e)}
                      />
                    </TableHead>
                    <TableHead className="relative group" style={{ width: getColumnWidth('email') }}>
                      <div className="pr-2">Email</div>
                      <div 
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                        onMouseDown={(e) => handleResizeStart('email', e)}
                      />
                    </TableHead>
                    <TableHead className="relative group" style={{ width: getColumnWidth('phone') }}>
                      <div className="pr-2">Phone</div>
                      <div 
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                        onMouseDown={(e) => handleResizeStart('phone', e)}
                      />
                    </TableHead>
                    <TableHead className="relative group" style={{ width: getColumnWidth('role') }}>
                      <div className="pr-2">Role</div>
                      <div 
                        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                        onMouseDown={(e) => handleResizeStart('role', e)}
                      />
                    </TableHead>
                    <TableHead className="text-right" style={{ width: getColumnWidth('actions') }}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map((contact) => (
                    <TableRow key={contact.id} data-testid={`row-contact-${contact.id}`}>
                      <TableCell>
                        <Link href={`/contacts/${contact.id}`}>
                          <span className="font-medium text-blue-600 hover:underline cursor-pointer inline-flex items-center gap-1" data-testid={`link-contact-${contact.id}`}>
                            {contact.firstName} {contact.lastName}
                            <ExternalLink className="w-3 h-3" />
                          </span>
                        </Link>
                        {contact.jobTitle && (
                          <p className="text-xs text-gray-500">{contact.jobTitle}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.companies && contact.companies.length > 0 ? (
                          <div className="space-y-1">
                            {contact.companies.map((companyRel) => (
                              <div key={`${contact.id}-${companyRel.companyId}`}>
                                <Link href={`/companies/${companyRel.companyId}`}>
                                  <span className="text-sm text-blue-600 hover:underline cursor-pointer inline-flex items-center gap-1">
                                    {companyRel.company.name}
                                    {companyRel.isPrimary && (
                                      <Badge variant="outline" className="text-xs ml-1">Primary</Badge>
                                    )}
                                  </span>
                                </Link>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.email ? (
                          <a href={`mailto:${contact.email}`} className="text-sm text-gray-700 hover:text-blue-600 inline-flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.phone ? (
                          <a href={`tel:${contact.phone}`} className="text-sm text-gray-700 hover:text-blue-600 inline-flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {formatPhoneNumber(contact.phone)}
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.companies && contact.companies.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {Array.from(new Set(contact.companies.map(c => c.relationshipType))).map(role => (
                              <Badge 
                                key={role}
                                variant="secondary" 
                                className={`${getRoleBadgeColor(role)} text-xs`}
                              >
                                {role}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <QuickNotePopover contactId={contact.id} />
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEditContact(contact)}
                            data-testid={`button-edit-contact-${contact.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteContact(contact.id)}
                            disabled={deleteContactMutation.isPending}
                            data-testid={`button-delete-contact-${contact.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}

        <div className="flex flex-col items-center gap-2 mt-4 px-4 pb-4">
          <p className="text-sm text-muted-foreground" aria-live="polite">
            Showing {contacts.length} of {contactsTotalCount}
          </p>
          {contactsHasNextPage && (
            <Button variant="outline" onClick={() => contactsFetchNextPage()} disabled={contactsIsFetchingNextPage}
              aria-label="Load more contacts" aria-busy={contactsIsFetchingNextPage}>
              {contactsIsFetchingNextPage ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
              ) : "Load more"}
            </Button>
          )}
        </div>

        <CreateContactModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateContact}
          isLoading={createContactMutation.isPending}
        />
        
        {selectedContact && (
          <EditContactModal
            open={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedContact(null);
            }}
            onSubmit={handleUpdateContact}
            isLoading={updateContactMutation.isPending}
            contact={selectedContact}
          />
        )}
      </div>
    </ResponsiveLayout>
  );
}
